/**
 * v2 columnGroups — 선택 하위항목에 따른 elementTable 컬럼 표시/숨김 (프론트).
 * 서버 lib/journal-schema/column-groups.js 와 동기화.
 */

const ELEMENT_INTERNAL_KEYS = new Set(['moisture', 'ash']);
const ELEMENT_FIXED_KEYS = new Set(['sampleNo', 'testCategory']);

export function normalizeCategoryLabel(label) {
	const raw = String(label || '').trim();
	const s = raw.toUpperCase();
	if (s === 'CL') return 'CI';
	const ko = {
		탄소: 'C',
		수소: 'H',
		질소: 'N',
		황: 'S',
		산소: 'O',
		염소: 'CI',
	};
	if (ko[raw]) return ko[raw];
	return s;
}

/** O(%) = 100-(C+H+N+Cl+S+수분+회분) — 수분·습윤 회분 모두 차감 */
export function buildOxygenFormulaExpr(selectedCategories) {
	const parts = ['(Number(item.ash)||0)', '(Number(item.moisture)||0)'];
	if (selectedCategories.has('C')) parts.push('(Number(item.carbon)||0)');
	if (selectedCategories.has('H')) parts.push('(Number(item.hydrogen)||0)');
	if (selectedCategories.has('N')) parts.push('(Number(item.nitrogen)||0)');
	if (selectedCategories.has('CI')) parts.push('(Number(item.chlorine)||0)');
	if (selectedCategories.has('S')) parts.push('(Number(item.sulfur)||0)');
	return `(item)=>100-(${parts.join('+')})`;
}

function visibleKeysForSelection(selectedCategories, groups, { showAll = false } = {}) {
	const visible = new Set(ELEMENT_FIXED_KEYS);
	ELEMENT_INTERNAL_KEYS.forEach(k => visible.add(k));
	if (!groups || typeof groups !== 'object') return visible;

	Object.entries(groups).forEach(([cat, keys]) => {
		if (showAll || selectedCategories.has(normalizeCategoryLabel(cat))) {
			(Array.isArray(keys) ? keys : []).forEach(k => visible.add(k));
		}
	});
	return visible;
}

/**
 * @param {object} journalTableForm
 * @param {Array} formData
 * @param {object} v2Samples  { children, samples, testId }
 * @param {function} getSelectedChildrenUnion  (samples, children, testId) => children[]
 * @param {function} getChildLabel  (child) => string
 */
export function applyV2ColumnGroupVisibility(
	journalTableForm,
	formData,
	v2Samples,
	getSelectedChildrenUnion,
	getChildLabel,
) {
	const meta = formData?.[2];
	const groups = meta?.columnGroups?.elementTable;
	const section = journalTableForm?.elementTable;
	if (!groups || !section?.headers?.length) return;

	const children = v2Samples?.children || [];
	const samples = v2Samples?.samples || [];
	const testId = v2Samples?.testId;
	if (!children.length) return;

	const selected = getSelectedChildrenUnion(samples, children, testId);
	const selectedCats = new Set(
		selected.map(c => normalizeCategoryLabel(getChildLabel(c))).filter(Boolean),
	);
	const hasExplicitSelection = samples.some(sample =>
		children.some(child =>
			Object.prototype.hasOwnProperty.call(
				sample?.childIsTest || {},
				child.testId || child.id,
			),
		),
	);
	// 레거시처럼 선택 정보 자체가 없을 때만 전체 노출한다.
	// 모든 자식이 명시적으로 false인 경우는 "전체 표시"가 아니라 실제 미선택 상태다.
	const showAll = selectedCats.size === 0 && !hasExplicitSelection;
	const visible = visibleKeysForSelection(selectedCats, groups, { showAll });

	section.headers = section.headers.map(h => {
		if (!h) return h;
		const key = h.key || h.headerKey;
		if (!key) return h;
		const copy = { ...h };
		if (ELEMENT_FIXED_KEYS.has(key)) {
			copy.isHide = false;
			return copy;
		}
		if (ELEMENT_INTERNAL_KEYS.has(key)) {
			copy.isHide = true;
			return copy;
		}
		if (key === 'oxygen') {
			if (showAll || selectedCats.has('O')) {
				const others = new Set(selectedCats);
				others.delete('O');
				copy.formula = buildOxygenFormulaExpr(
					others.size ? others : new Set(['C', 'H', 'N', 'S', 'CI']),
				);
				copy.isHide = false;
			} else {
				copy.isHide = true;
			}
			return copy;
		}
		copy.isHide = !visible.has(key);
		return copy;
	});
}
